import type { DateValue } from "@internationalized/date";
import { CalendarIcon } from "lucide-react";
import {
  DatePicker as AriaDatePicker,
  type DatePickerProps as AriaDatePickerProps,
  Button,
  Calendar,
  CalendarCell,
  CalendarGrid,
  DateInput,
  DateSegment,
  Dialog,
  Group,
  Heading,
} from "react-aria-components";
import { tv } from "tailwind-variants";

import { Popover } from "@/components/base/popover";
import { composeTailwindRenderProps, focusRing } from "@/components/base/utils";

import { Description, FieldError, fieldBorderStyles, Label } from "./field";

const groupStyles = tv({
  extend: focusRing,
  base: "flex items-center bg-white border-1 rounded-md overflow-hidden",
  variants: {
    isFocusWithin: fieldBorderStyles.variants.isFocusWithin,
    isInvalid: fieldBorderStyles.variants.isInvalid,
    isDisabled: fieldBorderStyles.variants.isDisabled,
  },
});

const inputStyles = tv({
  base: "flex px-2 py-1.5 text-sm flex-1",
});

const segmentStyles = tv({
  base: "inline rounded px-0.5 type-literal:px-0 caret-transparent outline outline-0 forced-color-adjust-none",
  variants: {
    isPlaceholder: {
      true: "text-gray-600 italic",
    },
    isDisabled: {
      true: "text-gray-400 forced-colors:text-[GrayText]",
    },
    isFocused: {
      true: "bg-blue-600 text-white forced-colors:bg-[Highlight] forced-colors:text-[HighlightText]",
    },
  },
});

const calendarCellStyles = tv({
  base: "w-8 h-8 text-sm rounded-lg flex items-center justify-center cursor-pointer forced-colors:text-[ButtonText]",
  variants: {
    isHovered: {
      true: "bg-gray-200",
    },
    isFocused: {
      true: "bg-blue-200 text-black forced-colors:bg-[Highlight] forced-colors:text-[HighlightText]",
    },
    isSelected: {
      true: "bg-blue-600 text-white",
    },
    isOutsideMonth: {
      true: "text-gray-400",
    },
    isDisabled: {
      true: "text-gray-300 cursor-default",
    },
  },
});

export type DatePickerProps<T extends DateValue> = Omit<
  AriaDatePickerProps<T>,
  "children"
> & {
  label?: string;
  description?: string;
  errorMessage?: string;
};

export function DatePicker<T extends DateValue>({
  label,
  description,
  errorMessage,
  ...props
}: DatePickerProps<T>) {
  return (
    <AriaDatePicker
      {...props}
      className={composeTailwindRenderProps(
        props.className,
        "flex flex-col gap-1",
      )}
    >
      {label && <Label>{label}</Label>}
      <Group className={groupStyles}>
        <DateInput className={inputStyles}>
          {(segment) => (
            <DateSegment segment={segment} className={segmentStyles} />
          )}
        </DateInput>
        <Button className="flex items-center self-stretch border-l border-gray-300 px-2 text-gray-600 hover:bg-gray-100">
          <CalendarIcon size={16} />
        </Button>
      </Group>
      {description && <Description>{description}</Description>}
      {errorMessage && <FieldError>{errorMessage}</FieldError>}
      <Popover>
        <Dialog className="p-3">
          <Calendar className="outline-none">
            <header className="mb-2 flex items-center justify-between">
              <Button
                slot="previous"
                className="flex h-8 w-8 items-center justify-center rounded-lg hover:bg-gray-200"
              >
                ◀
              </Button>
              <Heading className="text-sm font-medium" />
              <Button
                slot="next"
                className="flex h-8 w-8 items-center justify-center rounded-lg hover:bg-gray-200"
              >
                ▶
              </Button>
            </header>
            <CalendarGrid className="border-separate border-spacing-1">
              {(date) => (
                <CalendarCell date={date} className={calendarCellStyles} />
              )}
            </CalendarGrid>
          </Calendar>
        </Dialog>
      </Popover>
    </AriaDatePicker>
  );
}
